
import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarIcon, Upload, X, Info, Check, AlertTriangle, Loader2, Trash2 } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/lib/supabase";
import { useAuthSession } from "@/hooks/useAuthSession";

export interface CurrentAffairUploaderProps {
  onSuccess: () => void;
}

const CurrentAffairUploader: React.FC<CurrentAffairUploaderProps> = ({ onSuccess }) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [date, setDate] = useState<Date | undefined>();
  const [timePeriod, setTimePeriod] = useState('daily');
  const [language, setLanguage] = useState('english');
  const [tags, setTags] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState('manual');
  const { userId } = useAuthSession();
  
  const languages = [
    { value: "english", label: "English" },
    { value: "hindi", label: "Hindi" },
    { value: "tamil", label: "Tamil" },
    { value: "telugu", label: "Telugu" },
    { value: "kannada", label: "Kannada" },
    { value: "malayalam", label: "Malayalam" },
    { value: "marathi", label: "Marathi" },
    { value: "gujarati", label: "Gujarati" },
    { value: "punjabi", label: "Punjabi" },
    { value: "bengali", label: "Bengali" },
    { value: "urdu", label: "Urdu" }
  ];
  
  const timePeriods = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" }
  ];
  
  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error("Please enter a title");
      return;
    }
    
    if (!content.trim()) {
      toast.error("Please enter content");
      return;
    }
    
    if (!date) {
      toast.error("Please select a date");
      return;
    }
    
    if (!userId) {
      toast.error("You must be logged in to upload content");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Convert content to HTML if needed
      const processedContent = currentTab === 'html' ? content : convertToHtml(content);
      
      // Process tags
      const tagsList = tags.split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);
      
      // Format date as YYYY-MM-DD to avoid timezone issues
      const formattedDate = format(date, 'yyyy-MM-dd');
      
      const currentAffair = {
        title,
        content: processedContent,
        date: formattedDate, // Store only the date part without time
        time_period: timePeriod,
        language,
        created_by: userId,
        metadata: {
          tags: tagsList
        },
        category: 'Current Affairs'
      };
      
      // Insert into Supabase
      const { data, error } = await supabase
        .from('current_affairs')
        .insert([currentAffair])
        .select();
      
      if (error) {
        console.error("Error uploading current affair:", error);
        toast.error("Failed to upload content. " + error.message);
        return;
      }
      
      console.log("Current affair created:", data);
      
      // Success notification
      toast.success("Current affair content uploaded successfully");
      
      // Clear form
      resetForm();
      
      // Call success callback
      onSuccess();
      
    } catch (error) {
      console.error("Error uploading current affair:", error);
      toast.error("Failed to upload content. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Delete a current affair by ID
  const handleDelete = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this article? This action cannot be undone.")) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase
        .from('current_affairs')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error("Error deleting article:", error);
        toast.error("Failed to delete content. " + error.message);
        return;
      }
      
      toast.success("Article deleted successfully");
      onSuccess();
      
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Failed to delete article. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };
  
  // Convert plain text to basic HTML
  const convertToHtml = (text: string): string => {
    // Replace line breaks with <br>
    let html = text.replace(/\n{2,}/g, '</p><p>').replace(/\n/g, '<br>');
    
    // Wrap in paragraphs if not already
    if (!html.startsWith('<p>')) {
      html = `<p>${html}</p>`;
    }
    
    return html;
  };
  
  // Reset the form
  const resetForm = () => {
    setTitle('');
    setContent('');
    setDate(undefined);
    setTimePeriod('daily');
    setLanguage('english');
    setTags('');
    setCurrentTab('manual');
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <CardTitle>Upload Current Affairs Content</CardTitle>
          <CardDescription>Add new current affairs content to the database</CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList className="mb-4 grid grid-cols-2">
              <TabsTrigger value="manual">Plain Text</TabsTrigger>
              <TabsTrigger value="html">HTML Content</TabsTrigger>
            </TabsList>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Title Input */}
                <div className="space-y-2">
                  <Label htmlFor="title">Title</Label>
                  <Input 
                    id="title" 
                    placeholder="Enter the title" 
                    value={title} 
                    onChange={(e) => setTitle(e.target.value)}
                    required
                  />
                </div>
                
                {/* Content Input */}
                <div className="space-y-2">
                  <Label htmlFor="content">Content</Label>
                  <Textarea
                    id="content"
                    placeholder={currentTab === 'manual' 
                      ? "Enter the content in plain text" 
                      : "Enter the content in HTML format"
                    }
                    className="min-h-[200px]"
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Date Selector */}
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start text-left font-normal"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {date ? format(date, "PPP") : "Select date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Time Period Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="timePeriod">Time Period</Label>
                    <Select value={timePeriod} onValueChange={setTimePeriod}>
                      <SelectTrigger id="timePeriod">
                        <SelectValue placeholder="Select time period" />
                      </SelectTrigger>
                      <SelectContent>
                        {timePeriods.map((period) => (
                          <SelectItem key={period.value} value={period.value}>
                            {period.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Language Selector */}
                  <div className="space-y-2">
                    <Label htmlFor="language">Language</Label>
                    <Select value={language} onValueChange={setLanguage}>
                      <SelectTrigger id="language">
                        <SelectValue placeholder="Select language" />
                      </SelectTrigger>
                      <SelectContent>
                        {languages.map((lang) => (
                          <SelectItem key={lang.value} value={lang.value}>
                            {lang.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Tags Input */}
                  <div className="space-y-2">
                    <Label htmlFor="tags">Tags (comma separated)</Label>
                    <Input
                      id="tags"
                      placeholder="national, elections, economy, etc."
                      value={tags}
                      onChange={(e) => setTags(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </form>
          </Tabs>
        </CardContent>
        
        <CardFooter className="flex justify-between">
          <Button variant="outline" onClick={resetForm} disabled={isLoading}>
            <X className="h-4 w-4 mr-2" /> Clear
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" /> Upload Content
              </>
            )}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export { CurrentAffairUploader };
